import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import {
  FiPlus,
  FiSearch,
  FiRefreshCw,
  FiEdit,
  FiTrash2,
  FiInfo,
  FiSettings,
  FiKey,
  FiList,
  FiCopy
} from 'react-icons/fi';
import axios from 'axios';
import Button from '../../components/common/Button';
import Loader from '../../components/common/Loader';
import Modal from '../../components/common/Modal';
import { useAuth } from '../../context/AuthContext';

// 获取应用列表
const fetchApplications = async () => {
  const response = await axios.get('/api/applications');
  return response.data.applications;
};

// 删除应用
const deleteApplication = async (id) => {
  const response = await axios.delete(`/api/applications/${id}`);
  return response.data;
};

const ApplicationList = () => {
  const queryClient = useQueryClient();
  const { currentUser } = useAuth();
  const isAdmin = currentUser?.role === 'admin';
  
  // 状态
  const [search, setSearch] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteAppId, setDeleteAppId] = useState(null);
  
  // 查询应用列表
  const { data: applications, isLoading, error, refetch } = useQuery('applications', fetchApplications);
  
  // 删除应用突变
  const deleteMutation = useMutation(deleteApplication, {
    onSuccess: () => {
      toast.success('应用删除成功');
      queryClient.invalidateQueries('applications');
      setShowDeleteModal(false);
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || '删除应用失败');
    }
  });
  
  // 处理删除应用
  const handleDelete = () => {
    if (deleteAppId) {
      deleteMutation.mutate(deleteAppId);
    }
  };
  
  // 处理搜索
  const handleSearch = (e) => {
    e.preventDefault();
    // 简单实现前端过滤
  };
  
  // 清除搜索
  const clearSearch = () => {
    setSearch('');
  };
  
  // 过滤应用
  const filteredApps = applications
    ? applications.filter(app => 
        app.name.toLowerCase().includes(search.toLowerCase()) ||
        app.description?.toLowerCase().includes(search.toLowerCase())
      )
    : [];
  
  // 页面动画
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        when: 'beforeChildren',
        staggerChildren: 0.1
      }
    }
  };
  
  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.3
      }
    }
  };
  
  // 加载状态
  if (isLoading) {
    return (
      <div className="p-6 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div className="skeleton h-56" />
          <div className="skeleton h-56" />
          <div className="skeleton h-56" />
        </div>
      </div>
    );
  }
  
  // 错误状态
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-full">
        <div className="text-red-500 text-xl mb-4">获取应用列表失败</div>
        <Button onClick={() => refetch()}>重试</Button>
      </div>
    );
  }
  
  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="h-full"
    >
      {/* 页面标题 */}
      <motion.div variants={itemVariants} className="flex flex-wrap justify-between items-center gap-3 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-[#1f2328]">{isAdmin ? '全部应用' : '我的应用'}</h1>
          <p className="text-[#656d76] mt-1">{isAdmin ? '管理系统内所有用户的应用（超级管理员视角）' : '管理您的应用和授权系统'}</p>
        </div>
        <Button
          to="/applications/create"
          variant="primary"
          icon={<FiPlus />}
        >
          创建应用
        </Button>
      </motion.div>
      
      {/* 搜索栏 */}
      <motion.div variants={itemVariants} className="bg-white rounded-md p-4 mb-6">
        <form onSubmit={handleSearch} className="flex gap-4">
          <div className="relative flex-1">
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="搜索应用名称或描述..."
              className="w-full py-2 px-4 !pl-10 bg-[#f6f8fa] rounded-md text-[#1f2328] focus:outline-none input-primary"
            />
            <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#656d76]" />
            {search && (
              <button
                type="button"
                onClick={clearSearch}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-[#656d76] hover:text-[#1f2328]"
              >
                &times;
              </button>
            )}
          </div>
          
          <Button
            onClick={() => refetch()}
            variant="secondary"
            icon={<FiRefreshCw />}
            className="px-3"
          >
            刷新
          </Button>
        </form>
      </motion.div>
      
      {/* 应用列表 */}
      {filteredApps.length === 0 ? (
        <motion.div
          variants={itemVariants}
          className="bg-white rounded-md shadow-sm p-10 text-center"
        >
          <div className="text-[#656d76] mb-6">
            <div className="w-16 h-16 rounded-md brand-soft brand-text flex items-center justify-center mx-auto mb-4">
              <FiInfo className="text-3xl" />
            </div>
            <p className="text-xl text-[#24292f] font-medium">{isAdmin ? '系统内还没有任何应用' : '您还没有创建任何应用'}</p>
            <p className="mt-2">创建应用后，即可为 EA/软件生成和管理授权码</p>
          </div>
          {/* 三步上手引导 */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 max-w-2xl mx-auto mb-8 text-left">
            {[
              { step: '1', title: '创建应用', desc: '为您的 EA/软件建立应用，获得 appId' },
              { step: '2', title: '生成授权码', desc: '批量生成卡密，发放给客户激活' },
              { step: '3', title: '接入验证', desc: '客户端调用 verify 接口完成联网授权' }
            ].map((item) => (
              <div key={item.step} className="bg-[#f6f8fa] rounded-md p-4">
                <div className="w-7 h-7 rounded-full brand-gradient text-[#1f2328] text-sm font-bold flex items-center justify-center mb-2">
                  {item.step}
                </div>
                <p className="text-[#1f2328] text-sm font-medium">{item.title}</p>
                <p className="text-[#656d76] text-xs mt-1">{item.desc}</p>
              </div>
            ))}
          </div>
          <Button
            to="/applications/create"
            variant="primary"
            icon={<FiPlus />}
          >
            创建第一个应用
          </Button>
        </motion.div>
      ) : (
        <motion.div
          variants={itemVariants}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
        >
          {filteredApps.map(app => (
            <motion.div
  key={app.id}
  className="bg-white rounded-md shadow-sm overflow-hidden hover:shadow-sm transition-all duration-300"
  whileHover={{ y: -5 }}
>
  <div className="p-6">
    <h2 className="text-xl font-semibold text-[#1f2328] mb-2">{app.name}</h2>
    {isAdmin && (
      <p className="text-xs text-[#656d76] mb-2">
        归属:
        {app.owner ? (
          <span className="text-[#57606a] font-medium">
            {app.owner.username}
            {app.owner.id === currentUser?.id && <span className="brand-text"> (我)</span>}
          </span>
        ) : (
          <span className="text-[#8b949e]">未知用户</span>
        )}
      </p>
    )}
    <p className="text-[#656d76] mb-4 line-clamp-2">{app.description || '无描述'}</p>
    
  <div className="flex items-center gap-2 text-sm text-[#8b949e] mb-4 bg-[#f6f8fa] rounded-md px-3 py-2">
    <span>应用ID:</span>
    <span className="font-mono text-[#57606a] truncate flex-1">{app.appId}</span>
    <button
      onClick={() => {
        navigator.clipboard?.writeText(app.appId).then(() => toast.success('应用ID已复制')).catch(() => {});
      }}
      className="text-[#656d76] hover:text-[#1f2328] shrink-0"
      title="复制应用ID"
    >
      <FiCopy size={14} />
    </button>
  </div>
    

    <div className="mt-4 flex flex-col gap-2">
      <div className="flex gap-2 justify-between">

        <div className="flex-1">
          <Link
            to={`/applications/${app.id}`}
            className="flex items-center justify-center py-2 px-4 brand-soft brand-text hover:opacity-80 rounded-md transition-colors w-full"
          >
            <FiInfo className="mr-2" size={16} />
            查看详情
          </Link>
        </div>
        <div className="flex-1">
          <Link
            to={`/licenses/create?applicationId=${app.id}`}
            className="flex items-center justify-center py-2 px-4 bg-[#dafbe1] text-[#1a7f37] hover:bg-opacity-30 rounded-md transition-colors w-full"
          >
            <FiKey className="mr-2" size={16} />
            生成授权码
          </Link>
        </div>
      </div>
      
      <div className="flex gap-2 justify-between">

        <div className="flex-1">
          <Link
            to={`/applications/${app.id}/edit`}
            className="flex items-center justify-center py-2 px-4 bg-[#fff8c5] text-[#9a6700] hover:bg-opacity-30 rounded-md transition-colors w-full"
          >
            <FiEdit className="mr-2" size={16} />
            编辑应用
          </Link>
        </div>
        

        <div className="flex-1">
          <Link
            to={`/licenses?applicationId=${app.id}`}
            className="flex items-center justify-center py-2 px-4 bg-[#fbefff] text-[#8250df] hover:bg-opacity-30 rounded-md transition-colors w-full"
          >
            <FiList className="mr-2" size={16} />
            授权列表
          </Link>
        </div>
      </div>
      

      <button
        onClick={() => {
          setDeleteAppId(app.id);
          setShowDeleteModal(true);
        }}
        className="flex items-center justify-center py-2 px-4 mt-2 bg-[#ffebe9] text-[#cf222e] hover:bg-opacity-30 rounded-md transition-colors w-full"
      >
        <FiTrash2 className="mr-2" size={16} />
        删除应用
      </button>
    </div>
  </div>
</motion.div>
          ))}
        </motion.div>
      )}
      
      {/* 删除确认弹窗 */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title="删除应用"
      >
        <div className="mb-6">
          <p className="text-[#57606a]">确定要删除这个应用吗？此操作不可逆。</p>
          <p className="text-yellow-400 mt-4">
            <FiInfo className="inline-block mr-2" />
            删除应用将同时删除其下所有授权码、策略源、信号记录及相关指令。授权码需先手动清空；策略源需全部停用且停用满 7 天。
          </p>
        </div>
        <div className="flex justify-end gap-4">
          <Button
            onClick={() => setShowDeleteModal(false)}
            variant="secondary"
          >
            取消
          </Button>
          <Button
            onClick={handleDelete}
            variant="danger"
            icon={<FiTrash2 />}
            disabled={deleteMutation.isLoading}
          >
            {deleteMutation.isLoading ? '删除中...' : '删除'}
          </Button>
        </div>
      </Modal>
    </motion.div>
  );
};

export default ApplicationList;